import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { ApiError, apiFetch, violationMessage } from '../../lib/api';
import { AuthEndorsement, AuthWordmark } from '../../lib/auth-brand';
import { AuthPageShell } from '../../lib/auth-page-shell';
import { useAuth } from '../../lib/auth';
import { resolvePostLoginPath } from '../lib/registration-api';

interface OtpRequestResponse {
  requestId: string;
  expiresInSeconds: number;
}

interface OtpVerifyResponse {
  accessToken: string;
  parentGuardianId: string;
  isNewAccount: boolean;
}

function isAccountNotRegistered(err: unknown): boolean {
  return err instanceof ApiError
    && err.problem.violations?.some((v) => v.code === 'ACCOUNT_NOT_REGISTERED') === true;
}

export function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const { login, token, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRegisterHint, setShowRegisterHint] = useState(false);

  if (!loading && token) {
    return <Navigate to="/dashboard" replace />;
  }

  async function sendOtp(): Promise<void> {
    setBusy(true);
    setError(null);
    setShowRegisterHint(false);
    try {
      const result = await apiFetch<OtpRequestResponse>('/auth/otp/request', {
        method: 'POST',
        body: JSON.stringify({ email, purpose: 'LOGIN' }),
      });
      setRequestId(result.requestId);
      setOtp('');
    } catch (err) {
      if (isAccountNotRegistered(err)) {
        setShowRegisterHint(true);
      }
      setError(err instanceof ApiError ? violationMessage(err.problem) : 'Could not send OTP');
    } finally {
      setBusy(false);
    }
  }

  async function requestOtp(event: FormEvent): Promise<void> {
    event.preventDefault();
    await sendOtp();
  }

  async function verifyOtp(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!requestId) return;
    setBusy(true);
    setError(null);
    setShowRegisterHint(false);
    try {
      const result = await apiFetch<OtpVerifyResponse>('/auth/otp/verify', {
        method: 'POST',
        body: JSON.stringify({ requestId, otp }),
      });
      await login(result.accessToken);
      navigate(await resolvePostLoginPath());
    } catch (err) {
      if (isAccountNotRegistered(err)) {
        setShowRegisterHint(true);
      }
      setError(err instanceof ApiError ? violationMessage(err.problem) : 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthPageShell>
      <AuthWordmark />
      <div className="page-header page-header-centred">
        <h1>Sign in</h1>
        <p className="lede">
          {requestId
            ? `We sent a six-digit code to ${email}.`
            : 'Use the email address you registered with and we will send a six-digit code.'}
        </p>
      </div>

      <div className="card">
        {error ? (
          <div className="error-banner" role="alert">
            <p>{error}</p>
            {showRegisterHint ? (
              <p className="text-sm section-gap">
                New here? <Link to="/register">Register a child</Link> to create an account.
              </p>
            ) : null}
          </div>
        ) : null}

        {!requestId ? (
          <form onSubmit={requestOtp}>
            <div className="field">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
            <div className="section-gap">
              <button
                className="btn btn-lg btn-block"
                type="submit"
                disabled={busy || !email.includes('@')}
              >
                {busy ? 'Sending…' : 'Send verification code'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={verifyOtp}>
            <div className="field">
              <label htmlFor="otp">Verification code</label>
              <input
                id="otp"
                className="otp-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- the only
                // action on the screen once the code has been sent.
                autoFocus
                value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                pattern="[0-9]{6}"
              />
            </div>
            <div className="stack section-gap">
              <button
                className="btn btn-lg btn-block"
                type="submit"
                disabled={busy || otp.length !== 6}
              >
                {busy ? 'Verifying…' : 'Verify and continue'}
              </button>
              <button
                className="btn btn-ghost btn-block"
                type="button"
                disabled={busy}
                onClick={() => void sendOtp()}
              >
                {busy ? 'Sending…' : 'Resend code'}
              </button>
              <button
                className="btn btn-ghost btn-block"
                type="button"
                disabled={busy}
                onClick={() => {
                  setRequestId(null);
                  setOtp('');
                  setError(null);
                  setShowRegisterHint(false);
                }}
              >
                Use a different email
              </button>
            </div>
          </form>
        )}
      </div>

      <p className="text-sm text-muted text-center section-gap">
        New here? <Link to="/register">Start registration</Link>
      </p>
      <AuthEndorsement />
    </AuthPageShell>
  );
}
